import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import S3ApiService from '../../../services/s3-api.service';
import {
  ASSETS_ROOT,
  buildS3Key,
  formatFileSize,
  getFileTypeFromName,
  joinLogical,
  logicalToCrumbs,
  logicalToRelative,
  normalizeSlashes,
  parentLogical,
  s3KeyToLogical,
  stripTrailingSlash,
  trimSlashes,
} from './pathUtils';

// PLS builds set REACT_APP_ENV from the pipeline's PLS_ / PLSCLOUD_ prefix.
const IS_PLS = (process.env.REACT_APP_ENV || '').toLowerCase().startsWith('pls');

/**
 * Core data hook for the explorer. Owns the current logical path, the listing,
 * and exposes mutation actions (create folder, rename, move, delete, upload).
 *
 * It accepts `path` + `onPathChange` so the *parent* can keep the path in the URL
 * (via React Router) — the hook itself stays URL-agnostic.
 */
export default function useAssetBrowser({
  organizationName,
  sceneName,
  captionPathPrefix = '',
  path,
  onPathChange,
  rendererBaseUrl,
}) {
  const [contents, setContents] = useState({ folders: [], files: [] });
  // OPUS-1045: recursive size/count of the current folder (information point).
  const [folderStats, setFolderStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const requestId = useRef(0);

  const currentLogicalPath = useMemo(() => {
    const norm = trimSlashes(path || ASSETS_ROOT) || ASSETS_ROOT;
    if (norm === ASSETS_ROOT) return ASSETS_ROOT;
    return norm.startsWith(`${ASSETS_ROOT}/`) ? norm : `${ASSETS_ROOT}/${norm}`;
  }, [path]);

  const navigateTo = useCallback((nextLogical) => {
    const norm = trimSlashes(nextLogical || ASSETS_ROOT) || ASSETS_ROOT;
    const final = norm === ASSETS_ROOT || norm.startsWith(`${ASSETS_ROOT}/`)
      ? norm
      : `${ASSETS_ROOT}/${norm}`;
    onPathChange?.(final);
  }, [onPathChange]);

  const navigateUp = useCallback(() => {
    navigateTo(parentLogical(currentLogicalPath));
  }, [currentLogicalPath, navigateTo]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // -- list current folder ----------------------------------------------------
  useEffect(() => {
    if (!organizationName || !sceneName) return;
    const myId = ++requestId.current;
    setLoading(true);
    setError(null);

    const folderKey = buildS3Key({
      organizationName,
      sceneName,
      captionPathPrefix,
      logicalPath: currentLogicalPath,
    });
    const prefix = folderKey.endsWith('/') ? folderKey : `${folderKey}/`;

    // OPUS-1045: recursive folder size/count (no delimiter), shown as an info point.
    // Skipped on PLS: the no-delimiter recursive walk stalls on NAS-backed MinIO.
    setFolderStats(null);
    if (!IS_PLS) {
      S3ApiService.getFolderStats({ prefix, organizationName, sceneName })
        .then((stats) => { if (myId === requestId.current) setFolderStats(stats); })
        .catch(() => { if (myId === requestId.current) setFolderStats(null); });
    }

    S3ApiService.listObjects({ prefix, organizationName, sceneName, delimiter: '/' })
      .then((data) => {
        if (myId !== requestId.current) return; // stale
        const folders = (data?.folders || []).map((f) => {
          const logical = s3KeyToLogical({
            organizationName, sceneName, captionPathPrefix, fullKey: f.path,
          });
          return {
            ...f,
            type: 'folder',
            name: (f.name || '').replace(/\/$/, ''),
            logicalPath: logical,
            // backwards-compat alias used by existing consumers (SceneForm, JsonForm)
            path: logical,
            s3Key: stripTrailingSlash(f.path),
          };
        });
        const files = (data?.files || []).map((f) => {
          const logical = s3KeyToLogical({
            organizationName, sceneName, captionPathPrefix, fullKey: f.path,
          });
          return {
            ...f,
            type: 'file',
            fullPath: f.path,
            s3Key: f.path,
            logicalPath: logical,
            // backwards-compat alias used by existing consumers (SceneForm, JsonForm)
            path: logical,
            fileType: f.fileType || getFileTypeFromName(f.name),
            sizeLabel: formatFileSize(f.size),
          };
        });
        setContents({ folders, files });
        setLoading(false);
      })
      .catch((err) => {
        if (myId !== requestId.current) return;
        console.error('AssetExplorer: list failed', err);
        setError(err.message || 'Failed to list folder');
        setContents({ folders: [], files: [] });
        setLoading(false);
      });
  }, [organizationName, sceneName, captionPathPrefix, currentLogicalPath, refreshTick]);

  // -- breadcrumbs (built from path, decoupled from API roundtrip) -----------
  const breadcrumbs = useMemo(() => {
    const parts = logicalToCrumbs(currentLogicalPath);
    return parts.map((segment, idx) => {
      const slice = parts.slice(0, idx + 1).join('/');
      return {
        name: idx === 0 ? 'Assets' : segment,
        logicalPath: slice,
        rawName: segment,
        isRoot: idx === 0,
      };
    });
  }, [currentLogicalPath]);

  // -- image preview URL (renderer endpoint) ---------------------------------
  const buildPublicUrl = useCallback((fullKey) => {
    const base = (rendererBaseUrl || '').replace(/\/$/, '');
    return `${base}/${stripTrailingSlash(fullKey).replace(/^\/+/, '')}`;
  }, [rendererBaseUrl]);

  // -- mutations -------------------------------------------------------------
  const ctx = { organizationName, sceneName, captionPathPrefix };

  const createFolder = useCallback(async (name) => {
    const cleanName = (name || '').trim();
    if (!cleanName) throw new Error('Folder name is required');
    const logical = joinLogical(currentLogicalPath, cleanName);
    const key = buildS3Key({ ...ctx, logicalPath: logical });
    await S3ApiService.createFolder({
      path: `${key}/`,
      organizationName,
      sceneName,
    });
    refresh();
    return logical;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLogicalPath, organizationName, sceneName, captionPathPrefix, refresh]);

  const renameItem = useCallback(async (item, newName) => {
    const clean = (newName || '').trim();
    if (!clean || clean === item.name) return;
    const parts = (item.logicalPath || currentLogicalPath).split('/').filter(Boolean);
    parts.pop();
    parts.push(clean);
    const newLogical = parts.join('/');
    const srcKey = buildS3Key({ ...ctx, logicalPath: item.logicalPath });
    const dstKey = buildS3Key({ ...ctx, logicalPath: newLogical });
    await S3ApiService.moveObject({
      sourceKey: item.type === 'folder' ? `${srcKey}/` : srcKey,
      destinationKey: item.type === 'folder' ? `${dstKey}/` : dstKey,
      isFolder: item.type === 'folder',
      organizationName,
      sceneName,
    });
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLogicalPath, organizationName, sceneName, captionPathPrefix, refresh]);

  const moveItems = useCallback(async (items, destLogicalFolder) => {
    if (!items || items.length === 0) return;
    const destParts = (destLogicalFolder || ASSETS_ROOT).split('/').filter(Boolean);
    for (const item of items) {
      const srcParts = (item.logicalPath || '').split('/').filter(Boolean);
      const name = srcParts[srcParts.length - 1];
      if (!name) continue;
      const newLogical = [...destParts, name].join('/');
      // skip no-op
      if (newLogical === item.logicalPath) continue;
      // skip moving folder into itself
      if (item.type === 'folder' && (newLogical === item.logicalPath || newLogical.startsWith(`${item.logicalPath}/`))) continue;
      const srcKey = buildS3Key({ ...ctx, logicalPath: item.logicalPath });
      const dstKey = buildS3Key({ ...ctx, logicalPath: newLogical });
      // eslint-disable-next-line no-await-in-loop
      await S3ApiService.moveObject({
        sourceKey: item.type === 'folder' ? `${srcKey}/` : srcKey,
        destinationKey: item.type === 'folder' ? `${dstKey}/` : dstKey,
        isFolder: item.type === 'folder',
        organizationName,
        sceneName,
      });
    }
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationName, sceneName, captionPathPrefix, refresh]);

  const deleteItems = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    const keys = items.map((item) => {
      const key = buildS3Key({ ...ctx, logicalPath: item.logicalPath });
      return { key: item.type === 'folder' ? `${key}/` : key, isFolder: item.type === 'folder' };
    });
    await S3ApiService.bulkDelete({ keys, organizationName, sceneName });
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationName, sceneName, captionPathPrefix, refresh]);

  /**
   * Upload one or more files with up to N retries each. Files with webkitRelativePath
   * preserve their folder structure relative to currentLogicalPath.
   */
  const uploadFiles = useCallback(async (files, { onProgress, onFileDone, onFileError, retries = 2 } = {}) => {
    if (!files || files.length === 0) return { uploaded: 0, errors: [] };
    let done = 0;
    const errors = [];
    const concurrency = 6;
    const queue = Array.from(files);

    const next = async () => {
      const file = queue.shift();
      if (!file) return;
      // path the file should occupy *under the current folder* — never includes
      // currentLogicalPath itself. relativeToLogical_safe prepends the current
      // folder; passing it in here would double-stamp it (the "/TEST/gaurav/s2
      // appears twice in the breadcrumb" bug).
      const relative = file.webkitRelativePath || file.name;
      const folderRelative = logicalToRelative(currentLogicalPath);

      // ensure any parent dirs implied by webkitRelativePath exist
      const dirParts = relative.split('/').slice(0, -1);
      if (dirParts.length) {
        const dirSegments = [];
        for (const part of dirParts) {
          dirSegments.push(part);
          const dirLogical = relativeToLogical_safe(folderRelative, dirSegments);
          const dirKey = buildS3Key({ ...ctx, logicalPath: dirLogical });
          try {
            // eslint-disable-next-line no-await-in-loop
            await S3ApiService.createFolder({ path: `${dirKey}/`, organizationName, sceneName, suppressEvent: true });
          } catch (_) { /* may already exist */ }
        }
      }

      const destLogical = relativeToLogical_safe(folderRelative, relative.split('/'));
      const destKey = buildS3Key({ ...ctx, logicalPath: destLogical });

      let attempt = 0;
      let lastErr;
      while (attempt <= retries) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await S3ApiService.uploadObject({
            file,
            path: destKey,
            organizationName,
            sceneName,
            suppressEvent: true,
          });
          done += 1;
          onFileDone?.(file);
          onProgress?.(done, files.length);
          return next();
        } catch (err) {
          lastErr = err;
          attempt += 1;
        }
      }
      errors.push({ file: file.name, error: lastErr?.message || 'Upload failed' });
      onFileError?.(file, lastErr);
      done += 1;
      onProgress?.(done, files.length);
      return next();
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, next));
    refresh();
    return { uploaded: done - errors.length, errors };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLogicalPath, organizationName, sceneName, captionPathPrefix, refresh]);

  return {
    contents,
    folderStats,
    loading,
    error,
    currentLogicalPath,
    breadcrumbs,
    navigateTo,
    navigateUp,
    refresh,
    createFolder,
    renameItem,
    moveItems,
    deleteItems,
    uploadFiles,
    buildPublicUrl,
    buildS3KeyFor: (logical) => buildS3Key({ ...ctx, logicalPath: logical }),
  };
}

// internal helper used by uploadFiles only; kept here so the hook is self-contained.
function relativeToLogical_safe(folderRelative, parts) {
  const rel = [...(folderRelative ? folderRelative.split('/') : []), ...parts]
    .filter(Boolean)
    .join('/');
  return rel ? `${ASSETS_ROOT}/${rel}` : ASSETS_ROOT;
}
